<template>
    <v-scroll-y-transition>
        <v-toolbar
            v-show="show"
            absolute
            :light="$vuetify.theme.dark"
            :dark="!$vuetify.theme.dark"
            class="data-table-toolbar"
            :color="color"
            :height="height"
            :class="toolbarClass"
        >
            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        icon
                        aria-label="Cancel selection"
                        data-cy="toolbar-back"
                        v-on="on"
                        @click="onCancelClick"
                        ><v-icon>{{ icons.arrowBack }}</v-icon></v-btn
                    >
                </template>
                <span>Cancel</span>
            </v-tooltip>
            <v-toolbar-title class="px-2 mr-4 text-subtitle-1">
                {{ title }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <!-- ==== expanded toolbar items when we have space ==== -->
            <v-toolbar-items v-if="showExpandedItems">
                <v-tooltip
                    v-for="(item, index) in expandedItems"
                    :key="`tooltip-${index}`"
                    top
                >
                    <template v-slot:activator="{ on }">
                        <v-btn
                            :key="`action-btn-${index}`"
                            :color="item.color"
                            small
                            text
                            class="mx-1"
                            :data-cy="`toolbar-${item.name}`"
                            v-on="on"
                            @click="onItemClick(item)"
                        >
                            <v-icon
                                v-if="item.icon && item.icon.length"
                                class="mr-1"
                                >{{ icons[item.icon] }}</v-icon
                            >
                            <span v-if="item.text && item.text.length">{{
                                item.text
                            }}</span>
                        </v-btn>
                    </template>
                    <span>{{ item.tooltip || item.text }}</span>
                </v-tooltip>
            </v-toolbar-items>
            <!-- ==== collapsed toolbar items under a drop menu when we don't have space -->
            <v-menu
                v-if="showCollapsedItems"
                transition="slide-y-transition"
                offset-y
                :light="$vuetify.theme.dark"
                :dark="!$vuetify.theme.dark"
            >
                <template v-slot:activator="{ on }">
                    <v-btn
                        icon
                        aria-label="More actions"
                        data-cy="toolbar-more"
                        v-on="on"
                    >
                        <v-icon>{{ icons.moreItemsVert }}</v-icon>
                    </v-btn>
                </template>
                <v-list :color="color">
                    <v-tooltip
                        v-for="(item, index) in collapsedItems"
                        :key="`tooltip-${index}`"
                        left
                    >
                        <template v-slot:activator="{ on }">
                            <v-list-item
                                :key="`menu-item-${index}`"
                                dense
                                v-on="on"
                                @click="onItemClick(item)"
                            >
                                <v-list-item-icon
                                    v-if="item.icon && item.icon.length"
                                    class="mr-2"
                                >
                                    <v-icon>{{ icons[item.icon] }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title
                                    v-if="item.text && item.text.length"
                                    class="text-subtitle-2"
                                    :data-cy="`toolbar-${item.name}`"
                                >
                                    {{ item.text }}
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                        <span>{{ item.tooltip || item.text }}</span>
                    </v-tooltip>
                </v-list>
            </v-menu>
        </v-toolbar>
    </v-scroll-y-transition>
</template>

<script>
export default {
    name: 'FloatingToolbar',
    props: {
        show: {
            type: Boolean,
            required: false,
            default: false
        },
        title: {
            type: String,
            required: false,
            default: ''
        },
        color: {
            type: String,
            required: false,
            default: 'secondary'
        },
        height: {
            type: String,
            required: false,
            default: '40'
        },
        toolbarClass: {
            type: String,
            required: false,
            default: ''
        },
        items: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    computed: {
        expandedItems() {
            // contains the items displayed on the toolbar root
            let howMany;
            if (this.$vuetify.breakpoint.xs) {
                howMany = 0;
            } else if (this.$vuetify.breakpoint.sm) {
                howMany = 1;
            } else if (this.$vuetify.breakpoint.md) {
                howMany = 3;
            } else if (this.$vuetify.breakpoint.lg) {
                howMany = 5;
            } else {
                howMany = 7;
            }
            return this.items.slice(0, howMany);
        },
        collapsedItems() {
            return this.items.slice(this.expandedItems.length);
        },
        showExpandedItems() {
            return this.expandedItems.length > 0;
        },
        showCollapsedItems() {
            return this.collapsedItems.length > 0;
        }
    },
    methods: {
        // ========================== Events ========================
        onCancelClick() {
            this.$emit('cancel');
        },
        onItemClick(item) {
            this.$emit('item-click', { item });
        }
    }
};
</script>

<style lang="scss" scoped>
.data-table-toolbar {
    top: initial;
    right: 0;
    left: 0;
    z-index: 3;
    border-radius: 5px;
}
</style>
